import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, Check, ShieldCheck,
} from "lucide-react";
import SiteHeader from "../components/gridvest/SiteHeader";
import Footer from "../components/gridvest/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_TYPES = [
  "Cell Tower",
  "Billboard",
  "Rooftop Telecom",
  "Data Center",
  "Mixed Portfolio",
  "Municipality",
  "Strategic Stakeholder",
  "Other",
];

const NUM_RANGES = ["1", "2–5", "6–10", "11–25", "26+"];

const FOCUS = [
  "Cell tower agreements",
  "Billboard infrastructure",
  "Rooftop telecom",
  "Infrastructure continuity",
  "Renewal visibility",
];

const initial = {
  name: "", email: "", company: "", asset_type: "", num_assets: "", message: "",
};

export default function WalkthroughPage() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.asset_type) {
      toast.error("Please complete name, email, and asset type.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/walkthrough`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        asset_type: form.asset_type,
        num_assets: form.num_assets || null,
        message: form.message.trim() || null,
      });
      setDone(true);
      toast.success("Request received. We'll be in touch to schedule.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Please review the fields.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07111F] text-[#F8FAFC]" data-testid="walkthrough-page">
      <SiteHeader active="walkthrough" />

      <main className="relative">
        <div className="gv-grid-bg absolute inset-x-0 top-0 h-[460px]" aria-hidden />

        <section className="relative mx-auto max-w-6xl px-6 pb-28 pt-16 lg:px-10 lg:pt-24">
          <Link
            to="/"
            className="mb-10 inline-flex items-center gap-2 font-body text-base text-[#CBD5E1] transition-colors hover:text-[#F8FAFC]"
            data-testid="walkthrough-back-home"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <p className="mb-5 font-display text-sm font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
              Private Walkthrough
            </p>
            <h1 className="font-display text-[40px] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#F8FAFC] sm:text-5xl lg:text-[64px]">
              Schedule a Confidential
              <br className="hidden sm:block" /> Infrastructure Walkthrough.
            </h1>
            <p className="mt-7 max-w-3xl font-body text-lg leading-[1.75] text-[#E2E8F0] sm:text-xl">
              InfraYield is currently conducting private pilot walkthroughs with
              infrastructure owners, municipalities, and strategic infrastructure
              stakeholders.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            {/* FORM */}
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-8 sm:p-10 lg:col-span-7"
              data-testid="walkthrough-form"
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-7">
                <Field label="Name" required>
                  <input
                    type="text" required value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Doe" className={inputCls}
                    data-testid="walkthrough-name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email" required value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jane@company.com" className={inputCls}
                    data-testid="walkthrough-email"
                  />
                </Field>
                <Field label="Company / Organization" className="sm:col-span-2">
                  <input
                    type="text" value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Acme Infrastructure Holdings" className={inputCls}
                    data-testid="walkthrough-company"
                  />
                </Field>
                <Field label="Asset Type" required>
                  <select
                    value={form.asset_type}
                    onChange={(e) => update("asset_type", e.target.value)}
                    className={inputCls}
                    data-testid="walkthrough-asset-type"
                  >
                    <option value="" disabled>Select asset type</option>
                    {ASSET_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-[#0F1B2D]">{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Approximate Number of Assets">
                  <select
                    value={form.num_assets}
                    onChange={(e) => update("num_assets", e.target.value)}
                    className={inputCls}
                    data-testid="walkthrough-num-assets"
                  >
                    <option value="">Prefer not to say</option>
                    {NUM_RANGES.map((r) => (
                      <option key={r} value={r} className="bg-[#0F1B2D]">{r}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Message" className="sm:col-span-2">
                  <textarea
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="A short note about your portfolio or what you'd like to discuss."
                    rows={5}
                    className={inputCls + " resize-none"}
                    data-testid="walkthrough-message"
                  />
                </Field>
              </div>

              <div className="mt-9 flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
                <p className="font-body text-sm text-[#94A3B8]">
                  Confidential. Reviewed by our team. Not shared publicly.
                </p>
                <button
                  type="submit"
                  disabled={submitting || done}
                  className="inline-flex w-full items-center justify-center gap-2.5 rounded-md bg-[#22C55E] px-7 py-4 font-body text-base font-semibold text-[#07111F] transition-colors hover:bg-[#22C55E]/90 disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40 sm:w-auto"
                  data-testid="walkthrough-submit"
                >
                  {submitting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : done ? (
                    <><Check className="h-5 w-5" /> Scheduled</>
                  ) : (
                    <>Schedule Walkthrough <ArrowRight className="h-5 w-5" /></>
                  )}
                </button>
              </div>
            </motion.form>

            {/* SIDEBAR */}
            <motion.aside
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-6 lg:col-span-5"
            >
              <div className="overflow-hidden rounded-3xl border border-[#1E293B] bg-[#0F1B2D]">
                <div className="flex items-center gap-3 border-b border-[#1E293B] px-6 py-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#1E293B] bg-[#07111F] text-[#22C55E]">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <p className="font-display text-base font-semibold text-[#F8FAFC]">
                    Current Pilot Focus
                  </p>
                </div>
                <ul className="divide-y divide-[#1E293B]">
                  {FOCUS.map((f, i) => (
                    <li
                      key={f}
                      className="flex items-center gap-4 px-6 py-4"
                    >
                      <span className="font-display text-sm font-extrabold text-[#38BDF8]">
                        0{i + 1}
                      </span>
                      <span className="font-body text-base text-[#E2E8F0]">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-3xl border border-[#1E293B] bg-[#07111F]/60 p-6">
                <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-[#38BDF8]">
                  What to expect
                </p>
                <p className="mt-3 font-body text-base leading-[1.75] text-[#E2E8F0]">
                  A 10-minute confidential conversation. We'll show the private
                  owner dashboard and walk through how InfraYield organizes
                  agreements, renewals, and continuity for your portfolio.
                </p>
              </div>

              <p className="text-center font-body text-base text-[#CBD5E1]">
                Prefer email?{" "}
                <a
                  href="mailto:hello@infrayield.co"
                  className="font-semibold text-[#F8FAFC] underline underline-offset-4 decoration-[#38BDF8]/60 hover:decoration-[#F8FAFC]"
                >
                  hello@infrayield.co
                </a>
              </p>
            </motion.aside>
          </div>
        </section>
      </main>

      <Footer />
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: { background: "#0F1B2D", color: "#F8FAFC", border: "1px solid #1E293B", fontSize: "15px" },
        }}
      />
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-[#334155] bg-[#07111F] px-5 py-4 font-body text-base text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/40";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={"block " + className}>
      <span className="mb-2.5 inline-flex items-center gap-1.5 font-display text-sm font-semibold text-[#F8FAFC]">
        {label}
        {required && <span className="text-[#22C55E]" aria-label="required">*</span>}
      </span>
      {children}
    </label>
  );
}
